/**
 * Sticky mobile Add to Cart bar.
 *
 * Entire feature is gated behind the PostHog feature flag
 * `sticky-mobile-atc` (must resolve to the string 'test'). If
 * PostHog never becomes available, this fails closed -- the bar is
 * never shown -- rather than defaulting to visible.
 *
 * When enabled, an IntersectionObserver watches the main product
 * page's Add to Cart button: the sticky bar appears once that
 * button scrolls out of view, and disappears again when it's back
 * in view OR when the page footer scrolls into view (so the bar
 * never sits on top of the footer).
 *
 * Separately (and NOT gated behind the flag), this file also
 * subscribes to the theme's cart-update pub/sub event and reports a
 * PostHog `add_to_cart` event for every successful add-to-cart on a
 * product page -- whether it came from the main Add to Cart button
 * or the sticky bar's button, since both submit the same
 * `product-form` element and both raise PUB_SUB_EVENTS.cartUpdate
 * on success. This gives the sticky-mobile-atc experiment a real
 * conversion metric that's directly comparable between the control
 * and test groups (control shoppers can still only use the main
 * button; test shoppers can use either), and doubles as general
 * add-to-cart tracking for the store going forward.
 */
(function () {
  'use strict';

  var FLAG_KEY = 'sticky-mobile-atc';
  var FLAG_VALUE = 'test';
  var POSTHOG_WAIT_MS = 4000;
  var POSTHOG_POLL_INTERVAL_MS = 100;

  function whenPosthogReady(callback) {
    var waited = 0;

    var poll = setInterval(function () {
      waited += POSTHOG_POLL_INTERVAL_MS;

      if (window.posthog && typeof window.posthog.getFeatureFlag === 'function') {
        clearInterval(poll);

        if (typeof window.posthog.onFeatureFlags === 'function') {
          // Fires once flags have loaded, or immediately if they
          // already have.
          window.posthog.onFeatureFlags(function () {
            callback(true);
          });
        } else {
          callback(true);
        }
        return;
      }

      if (waited >= POSTHOG_WAIT_MS) {
        clearInterval(poll);
        callback(false); // fail closed
      }
    }, POSTHOG_POLL_INTERVAL_MS);
  }

  function initStickyBar(bar) {
    var targetButtonId = bar.getAttribute('data-target-button');
    var targetButton = targetButtonId ? document.getElementById(targetButtonId) : null;
    var ctaButton = bar.querySelector('[data-sticky-add-to-cart-button]');

    if (!targetButton || !('IntersectionObserver' in window)) return;

    var bottomSentinel =
      document.querySelector('[id^="shopify-section-footer-group"]') ||
      document.querySelector('.footer') ||
      document.querySelector('footer');

    var mainButtonInView = true;
    var nearBottom = false;

    function updateVisibility() {
      var shouldShow = !mainButtonInView && !nearBottom;
      bar.classList.toggle('sticky-add-to-cart--visible', shouldShow);
      bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    }

    var mainObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          mainButtonInView = entry.isIntersecting;
          updateVisibility();
        });
      },
      { root: null, threshold: 0 }
    );
    mainObserver.observe(targetButton);

    if (bottomSentinel) {
      var bottomObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            nearBottom = entry.isIntersecting;
            updateVisibility();
          });
        },
        { root: null, threshold: 0 }
      );
      bottomObserver.observe(bottomSentinel);
    }

    if (ctaButton) {
      ctaButton.addEventListener('click', function () {
        if (window.posthog && typeof window.posthog.capture === 'function') {
          window.posthog.capture('sticky_atc_click');
        }
        // No preventDefault: the button's form="..." attribute
        // submits the real product-form the same way the cart
        // drawer's checkout button submits CartDrawer-Form.
      });
    }
  }

  /**
   * Reports a PostHog `add_to_cart` event for every successful
   * cart-update pub/sub event on this page, regardless of which
   * button triggered it and regardless of the sticky-mobile-atc
   * flag value. Runs unconditionally (not behind whenPosthogReady's
   * fail-closed gate) since a missed PostHog load here should just
   * mean a missed event, not a hidden feature.
   */
  function initAddToCartTracking() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.cartUpdate) {
      return;
    }

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!window.posthog || typeof window.posthog.capture !== 'function') return;

      var item = (event && event.cartData) || {};

      window.posthog.capture('add_to_cart', {
        product_id: item.product_id,
        variant_id: item.variant_id || (event && event.productVariantId),
        product_title: item.title,
        price: item.price,
        quantity: item.quantity,
        source: event && event.source,
      });
    });
  }

  function init() {
    var bars = document.querySelectorAll('[data-sticky-add-to-cart]');

    initAddToCartTracking();

    if (!bars.length) return;

    whenPosthogReady(function (ready) {
      if (!ready) return; // fail closed: PostHog never showed up

      var flagValue;
      try {
        flagValue = window.posthog.getFeatureFlag(FLAG_KEY);
      } catch (e) {
        return; // fail closed
      }

      if (flagValue !== FLAG_VALUE) return;

      Array.prototype.forEach.call(bars, initStickyBar);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
